import clsx from 'clsx';
import { ReactElement, useCallback, useMemo } from 'react';
import { Check } from 'react-feather';

import { Button } from './Button';
import style from './ButtonsSelect.module.css';

export interface ButtonsSelectOption<T> {
  value: T;
  content: string | ReactElement;
}

export interface ButtonsSelectProps<T> {
  id: string;
  selectedOptions: T[];
  options: ButtonsSelectOption<T>[];
  onChange: (selectedOptions: T[]) => void;
  maxSelected?: number;
}
export const ButtonsSelect = <T,>({ id, selectedOptions, options, maxSelected, onChange }: ButtonsSelectProps<T>) => {
  const internalSelectedOptions = useMemo(() => selectedOptions, [selectedOptions]);

  const usedMaxSelected = useMemo(() => {
    if (maxSelected === undefined) {
      return 1;
    }
    if (maxSelected === 0) {
      return Number.MAX_SAFE_INTEGER;
    }
    return maxSelected;
  }, [maxSelected]);

  const onButtonClick = useCallback(
    (value: T) => () => {
      const currentCheckIndex = internalSelectedOptions.indexOf(value);
      const isChecked = currentCheckIndex !== -1;
      // remove if already checked
      if (isChecked) {
        internalSelectedOptions.splice(currentCheckIndex, 1);
      } else {
        // remove all
        if (usedMaxSelected === 1) {
          internalSelectedOptions.splice(0, 1000);
        }
        // don't continue if one
        else if (internalSelectedOptions.length >= usedMaxSelected) {
          return;
        }

        internalSelectedOptions.push(value);
      }

      onChange(internalSelectedOptions);
    },
    [internalSelectedOptions, onChange, usedMaxSelected]
  );
  const optionsMarkup = useMemo(() => {
    return options.map((option) => {
      const { value, content } = option;
      const isChecked = internalSelectedOptions.indexOf(value) !== -1;
      const checkmark = isChecked ? <Check color="#ffffff" /> : undefined;
      const buttonContent = (
        <div className={clsx(style.option)}>
          <div className={clsx(style.content, { [style.checked]: isChecked })}>{content}</div>
          <div className={clsx(style.check)}>{checkmark}</div>
        </div>
      );
      return (
        <div key={`${value}`} className={clsx(style.optionWrapper)}>
          <Button outline={!isChecked} content={buttonContent} onClick={onButtonClick(value)} noPadding />
        </div>
      );
    });
  }, [internalSelectedOptions, onButtonClick, options]);
  return (
    <div key={id} className={clsx(style.root)}>
      {optionsMarkup}
    </div>
  );
};
